/**
 * Form enhancement module
 * Progressive enhancements for form interactions
 */

import { debounce, gettext, ngettextFormat } from './utils.js';
import { validateField, showFieldErrors, clearFieldErrors } from './validation.js';

/**
 * Auto-resize textarea based on content
 * @param {HTMLTextAreaElement} textarea - Textarea element
 */
export function autoResizeTextarea(textarea) {
    const resize = () => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    };
    
    // Initial resize
    resize();
    
    // Resize on input
    textarea.addEventListener('input', resize);
    
    // Handle programmatic value changes
    const observer = new MutationObserver(resize);
    observer.observe(textarea, {
        attributes: true,
        attributeFilter: ['value']
    });
    
    return observer;
}

/**
 * Initialize auto-resize for all textareas
 * @param {HTMLElement} container - Container element
 */
export function initAutoResizeTextareas(container = document) {
    const textareas = container.querySelectorAll('textarea[data-auto-resize]');
    textareas.forEach(textarea => autoResizeTextarea(textarea));
}

/**
 * Format input value as user types
 * @param {HTMLInputElement} input - Input element
 * @param {string} format - Format type (phone, currency, etc.)
 */
export function formatInput(input, format) {
    const formatters = {
        phone: (value) => {
            // Format as: +371 XXXX XXXX
            const cleaned = value.replace(/\D/g, '');
            if (cleaned.startsWith('371')) {
                const rest = cleaned.slice(3);
                if (rest.length <= 4) {
                    return `+371 ${rest}`;
                } else {
                    return `+371 ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                }
            }
            return value;
        },
        
        currency: (value) => {
            // Remove non-numeric except decimal
            const cleaned = value.replace(/[^\d.,]/g, '');
            // Replace comma with dot
            const normalized = cleaned.replace(',', '.');
            // Limit to 2 decimal places
            const parts = normalized.split('.');
            if (parts.length > 1) {
                return parts[0] + '.' + parts[1].slice(0, 2);
            }
            return normalized;
        },
        
        number: (value) => {
            return value.replace(/\D/g, '');
        },
        
        uppercase: (value) => {
            return value.toUpperCase();
        },
        
        lowercase: (value) => {
            return value.toLowerCase();
        }
    };
    
    const formatter = formatters[format];
    if (!formatter) return;
    
    input.addEventListener('input', (e) => {
        const cursorPos = e.target.selectionStart;
        const oldValue = e.target.value;
        const newValue = formatter(oldValue);
        
        if (oldValue !== newValue) {
            e.target.value = newValue;
            // Restore cursor position
            const diff = newValue.length - oldValue.length;
            e.target.setSelectionRange(cursorPos + diff, cursorPos + diff);
        }
    });
}

/**
 * Initialize input formatting
 * @param {HTMLElement} container - Container element
 */
export function initInputFormatting(container = document) {
    const inputs = container.querySelectorAll('input[data-format]');
    inputs.forEach(input => {
        const format = input.getAttribute('data-format');
        formatInput(input, format);
    });
}

/**
 * Character counter for inputs and textareas
 * @param {HTMLElement} field - Input or textarea element
 */
export function initCharacterCounter(field) {
    const maxLength = field.getAttribute('maxlength');
    if (!maxLength) return;
    
    // Create counter element
    const counter = document.createElement('div');
    counter.className = 'text-xs text-gray-500 mt-1 text-right';
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');
    
    const updateCounter = () => {
        const current = field.value.length;
        const remaining = maxLength - current;
        counter.textContent = ngettextFormat(
            '%(count)s character remaining', '%(count)s characters remaining', remaining, { count: remaining }
        );
        
        if (remaining < 20) {
            counter.classList.add('text-yellow-600');
        } else {
            counter.classList.remove('text-yellow-600');
        }
        
        if (remaining < 10) {
            counter.classList.add('text-red-600');
            counter.classList.remove('text-yellow-600');
        } else {
            counter.classList.remove('text-red-600');
        }
    };
    
    // Initial update
    updateCounter();
    
    // Update on input
    field.addEventListener('input', updateCounter);
    
    // Insert counter after field
    field.parentNode.insertBefore(counter, field.nextSibling);
}

/**
 * Initialize character counters
 * @param {HTMLElement} container - Container element
 */
export function initCharacterCounters(container = document) {
    const fields = container.querySelectorAll('input[maxlength][data-show-counter], textarea[maxlength][data-show-counter]');
    fields.forEach(field => initCharacterCounter(field));
}

/**
 * Toggle password visibility
 * @param {HTMLInputElement} input - Password input
 */
export function initPasswordToggle(input) {
    const wrapper = document.createElement('div');
    wrapper.className = 'relative';
    
    // Wrap the input
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(input);
    
    // Create toggle button
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600';
    toggle.setAttribute('aria-label', gettext('Toggle password visibility'));
    
    toggle.innerHTML = `
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    `;
    
    const eyeOpen = toggle.innerHTML;
    const eyeClosed = `
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
    `;
    
    toggle.addEventListener('click', () => {
        if (input.type === 'password') {
            input.type = 'text';
            toggle.innerHTML = eyeClosed;
        } else {
            input.type = 'password';
            toggle.innerHTML = eyeOpen;
        }
    });
    
    wrapper.appendChild(toggle);
}

/**
 * Initialize password toggles
 * @param {HTMLElement} container - Container element
 */
export function initPasswordToggles(container = document) {
    const inputs = container.querySelectorAll('input[type="password"][data-toggle-password]');
    inputs.forEach(input => initPasswordToggle(input));
}

/**
 * File input preview
 * @param {HTMLInputElement} input - File input
 */
export function initFilePreview(input) {
    const previewContainer = document.createElement('div');
    previewContainer.className = 'mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2';
    
    input.parentNode.insertBefore(previewContainer, input.nextSibling);
    
    input.addEventListener('change', (e) => {
        previewContainer.innerHTML = '';
        const files = Array.from(e.target.files);
        
        files.forEach(file => {
            const preview = document.createElement('div');
            preview.className = 'relative group';
            
            if (file.type.startsWith('image/')) {
                const img = document.createElement('img');
                img.className = 'w-full h-24 object-cover rounded';
                img.src = URL.createObjectURL(file);
                preview.appendChild(img);
            } else {
                const icon = document.createElement('div');
                icon.className = 'w-full h-24 bg-gray-100 rounded flex items-center justify-center';
                icon.innerHTML = `
                    <svg class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                `;
                preview.appendChild(icon);
            }
            
            const name = document.createElement('p');
            name.className = 'text-xs text-gray-600 mt-1 truncate';
            name.textContent = file.name;
            preview.appendChild(name);
            
            previewContainer.appendChild(preview);
        });
    });
}

/**
 * Initialize file previews
 * @param {HTMLElement} container - Container element
 */
export function initFilePreviews(container = document) {
    const inputs = container.querySelectorAll('input[type="file"][data-preview]');
    inputs.forEach(input => initFilePreview(input));
}

/**
 * Initialize all form enhancements
 * @param {HTMLElement} container - Container element
 */
export function initFormEnhancements(container = document) {
    initAutoResizeTextareas(container);
    initInputFormatting(container);
    initCharacterCounters(container);
    initPasswordToggles(container);
    initFilePreviews(container);
}

/**
 * Form submission handler with loading state
 * @param {HTMLFormElement} form - Form element
 * @param {Object} options - Options
 */
export function handleFormSubmit(form, options = {}) {
    const {
        onSubmit = null,
        onSuccess = null,
        onError = null,
        resetOnSuccess = false
    } = options;
    
    form.addEventListener('submit', async (e) => {
        const submitButton = form.querySelector('[type="submit"]');
        const originalText = submitButton ? submitButton.textContent : '';
        
        try {
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.textContent = gettext('Loading...');
                submitButton.classList.add('opacity-75');
            }
            
            if (onSubmit) {
                await onSubmit(e);
            }
            
            if (onSuccess) {
                onSuccess();
            }
            
            if (resetOnSuccess) {
                form.reset();
            }
        } catch (error) {
            if (onError) {
                onError(error);
            }
        } finally {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = originalText;
                submitButton.classList.remove('opacity-75');
            }
        }
    });
}